import { Link } from "react-router-dom";
import { useState } from "react";
import { Anchor, Shield } from "lucide-react";
import { toast } from "sonner";
import { subscribeNewsletter } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import ShareYourRitual from "@/components/ShareYourRitual";
import InsidersBox from "@/components/InsidersBox";

export default function Footer() {
    const { t } = useLang();
    const [email, setEmail] = useState("");
    const [loading, setLoading] = useState(false);

    const onSubmit = async (e) => {
        e.preventDefault();
        if (!email.includes("@")) {
            toast.error("Enter a valid email");
            return;
        }
        setLoading(true);
        try {
            await subscribeNewsletter(email);
            toast.success("You're on the list — we'll email when the first bar drops");
            setEmail("");
        } catch {
            toast.error("Something went wrong");
        } finally {
            setLoading(false);
        }
    };

    return (
        <footer
            className="bg-forest text-cream relative overflow-hidden"
            data-testid="site-footer"
        >
            <div className="grain absolute inset-0" />
            <div className="relative max-w-7xl mx-auto px-6 md:px-10 pt-20 pb-10">
                <div className="grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-8">
                    <div className="md:col-span-5">
                        <h3 className="font-serif text-4xl md:text-5xl leading-tight text-glow-gold">
                            {t("footer.waitlist_h")}
                        </h3>
                        <p className="mt-5 text-cream/70 max-w-md text-sm leading-relaxed">
                            {t("footer.waitlist_p")}
                        </p>
                        <form
                            onSubmit={onSubmit}
                            className="mt-8 flex items-center border-b border-cream/30 pb-2 max-w-md"
                            data-testid="newsletter-form"
                        >
                            <input
                                type="email"
                                placeholder="your@email.com"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                className="flex-1 bg-transparent outline-none text-cream placeholder:text-cream/40 py-2"
                                data-testid="newsletter-email-input"
                            />
                            <button
                                type="submit"
                                disabled={loading}
                                className="text-[0.7rem] tracking-[0.3em] uppercase text-gold hover:text-cream transition-colors"
                                data-testid="newsletter-submit"
                            >
                                {loading ? "…" : "Subscribe →"}
                            </button>
                        </form>
                        <InsidersBox />
                    </div>

                    <div className="md:col-span-2">
                        <h5 className="text-[0.68rem] tracking-[0.3em] uppercase text-gold mb-4">
                            Shop
                        </h5>
                        <ul className="space-y-2 text-sm text-cream/80">
                            <li>
                                <Link to="/inventory" className="hover:text-gold">
                                    First Drop
                                </Link>
                            </li>
                            <li>
                                <Link to="/shop" className="hover:text-gold">
                                    All Products
                                </Link>
                            </li>
                            <li>
                                <Link to="/craft" className="hover:text-gold">
                                    Watch the Craft
                                </Link>
                            </li>
                        </ul>
                    </div>

                    <div className="md:col-span-2">
                        <h5 className="text-[0.68rem] tracking-[0.3em] uppercase text-gold mb-4">
                            Hub
                        </h5>
                        <ul className="space-y-2 text-sm text-cream/80">
                            <li>
                                <Link to="/story" className="hover:text-gold">
                                    Our Story
                                </Link>
                            </li>
                            <li>
                                <Link to="/about" className="hover:text-gold">
                                    The Craft
                                </Link>
                            </li>
                            <li>
                                <span>CyberKids · 10%</span>
                            </li>
                            <li>
                                <Link
                                    to="/partner/auth"
                                    className="hover:text-gold"
                                    data-testid="footer-partner-signin"
                                >
                                    Partner sign in
                                </Link>
                            </li>
                        </ul>
                    </div>

                    <div className="md:col-span-3">
                        <h5 className="text-[0.68rem] tracking-[0.3em] uppercase text-gold mb-4">
                            Mailing address
                        </h5>
                        <p
                            className="text-sm text-cream/80 leading-relaxed"
                            data-testid="footer-address"
                        >
                            95-390 Kuahelani Avenue
                            <br />
                            3AC-1320
                            <br />
                            Mililani, HI 96789
                        </p>
                        <div className="mt-5 space-y-1 text-[0.7rem] tracking-[0.2em] uppercase text-cream/60">
                            <a
                                href="mailto:aloha@islandgoldglow.com"
                                className="block hover:text-gold transition-colors"
                                data-testid="footer-email-aloha"
                            >
                                aloha@islandgoldglow.com
                            </a>
                            <a
                                href="mailto:wholesale@islandgoldglow.com"
                                className="block hover:text-gold transition-colors"
                                data-testid="footer-email-wholesale"
                            >
                                wholesale@islandgoldglow.com
                            </a>
                        </div>
                        <div className="mt-5 flex items-center gap-4 text-cream/70">
                            <a
                                href="https://instagram.com/islandgoldglow"
                                target="_blank"
                                rel="noopener noreferrer"
                                aria-label="Instagram"
                                className="hover:text-gold"
                            >
                                <i className="fa-brands fa-instagram text-lg" />
                            </a>
                            <a
                                href="https://tiktok.com/@islandgoldglow"
                                target="_blank"
                                rel="noopener noreferrer"
                                aria-label="TikTok"
                                className="hover:text-gold"
                            >
                                <i className="fa-brands fa-tiktok text-lg" />
                            </a>
                            <a
                                href="https://youtube.com/@islandgoldglow"
                                target="_blank"
                                rel="noopener noreferrer"
                                aria-label="YouTube"
                                className="hover:text-gold"
                            >
                                <i className="fa-brands fa-youtube text-lg" />
                            </a>
                            <a
                                href="https://pinterest.com/islandgoldglow"
                                target="_blank"
                                rel="noopener noreferrer"
                                aria-label="Pinterest"
                                className="hover:text-gold"
                            >
                                <i className="fa-brands fa-pinterest text-lg" />
                            </a>
                        </div>
                    </div>
                </div>

                {/* CyberKids 10% Give-Back band */}
                <div
                    className="mt-14 px-5 md:px-8 py-4 bg-gold/10 border border-gold/30 rounded-sm flex flex-col md:flex-row items-start md:items-center justify-between gap-3"
                    data-testid="cyberkids-anchor-band"
                >
                    <div className="flex items-start md:items-center gap-3">
                        <Anchor
                            size={18}
                            className="shrink-0 mt-0.5 md:mt-0 text-gold"
                            aria-hidden
                        />
                        <p className="text-[0.7rem] md:text-[0.75rem] tracking-[0.22em] md:tracking-[0.28em] uppercase leading-snug text-cream">
                            <span className="text-gold font-semibold">
                                10% of every bar sold
                            </span>{" "}
                            funds{" "}
                            <span className="font-semibold">
                                CyberKids Academy
                            </span>{" "}
                            — code-literacy for island youth.
                        </p>
                    </div>
                    <Link
                        to="/mission"
                        className="text-[0.62rem] md:text-[0.66rem] tracking-[0.3em] uppercase font-semibold text-cream hover:text-gold transition-colors duration-300 inline-flex items-center gap-2"
                        data-testid="cyberkids-anchor-link"
                    >
                        See Our Mission →
                    </Link>
                </div>

                {/* Compliance & Trust */}
                <div
                    className="mt-6 px-5 md:px-8 py-5 bg-forest-deep/40 border border-cream/10 rounded-sm"
                    data-testid="compliance-trust"
                >
                    <div className="flex items-center gap-2 text-[0.6rem] md:text-[0.65rem] tracking-[0.32em] uppercase text-gold mb-3">
                        <Shield size={12} aria-hidden /> Trust &amp; Compliance
                    </div>
                    <p className="text-[0.72rem] md:text-[0.78rem] leading-[1.7] text-cream/75">
                        Stripe-encrypted checkout · TLS 1.3 in transit, AES-256
                        at rest · Handmade in small batches with full ingredient
                        traceability · Shipping within 2–5 business days of
                        drop · No customer data sold or shared with third
                        parties · GDPR &amp; CCPA aligned · 14-day return
                        window on unopened bars · All affiliate links disclosed
                        at the point of pairing.
                    </p>
                </div>

                <div className="mt-10 pt-8 border-t border-cream/15 flex flex-col md:flex-row items-start md:items-center justify-between gap-4 text-[0.7rem] tracking-[0.2em] uppercase text-cream/50">
                    <span>© {new Date().getFullYear()} Island Gold Glow Soap Co. · Handmade in Hawaiʻi</span>
                    <nav
                        className="flex flex-wrap items-center gap-x-5 gap-y-2"
                        data-testid="legal-shield-footer"
                        aria-label="Legal links"
                    >
                        <a
                            href="https://legal.eigooinc.net"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="hover:text-gold transition-colors"
                            data-testid="legal-terms-link"
                        >
                            Terms
                        </a>
                        <span aria-hidden className="text-cream/30">·</span>
                        <a
                            href="https://legal.eigooinc.net"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="hover:text-gold transition-colors"
                            data-testid="legal-privacy-link"
                        >
                            Privacy
                        </a>
                        <span aria-hidden className="text-cream/30">·</span>
                        <a
                            href="https://legal.eigooinc.net"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="hover:text-gold transition-colors"
                            data-testid="legal-shield-link"
                        >
                            Powered by EIGOO Legal Shield
                        </a>
                    </nav>
                    <span>islandgold.eigooinc.net</span>
                </div>
            </div>

            {/* Customer Feedback — Share Your Ritual */}
            <div className="relative max-w-7xl mx-auto px-6 md:px-10 pb-14">
                <ShareYourRitual />
            </div>
        </footer>
    );
}
